import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/schemeIssueMapping.api";
import { SchemeIssueMappingPayload } from "../types/schemeIssueMapping.type";

export const SCHEME_ISSUE_MAPPING_QUERY_KEY = ["scheme-issue-mapping"];
export const SCHEME_ISSUE_MAPPING_SUMMARY_QUERY_KEY = [
	"scheme-issue-mapping-summary",
];

export const useSchemeIssueMappings = (
	page = 1,
	limit = 10,
	schemeId?: number | string | null,
	issueTypeId?: number | string | null,
) => {
	const query = useQuery({
		queryKey: [
			...SCHEME_ISSUE_MAPPING_QUERY_KEY,
			page,
			limit,
			schemeId,
			issueTypeId,
		],
		queryFn: () =>
			api.fetchSchemeIssueMappings(page, limit, schemeId, issueTypeId),
	});

	return {
		...query,
		mappings: query.data?.items ?? [],
		totalCount: query.data?.totalItems ?? 0,
		totalPages: query.data?.totalPages ?? 0,
	};
};

// Looks up the single existing mapping row for a scheme + issue type combo
// (issueTypeId: null means the "no type" bucket). Used by the mapping form so
// a fresh "Map Issues" save merges into what's already mapped instead of
// silently replacing it.
export const useExistingSchemeIssueMapping = (
	schemeId: number | string | null | undefined,
	issueTypeId: number | string | null | undefined,
	enabled: boolean,
) => {
	const typeParam = issueTypeId === null ? "null" : issueTypeId;
	const query = useQuery({
		queryKey: [...SCHEME_ISSUE_MAPPING_QUERY_KEY, "single", schemeId, typeParam],
		queryFn: () => api.fetchSchemeIssueMappings(1, 1, schemeId, typeParam),
		enabled: enabled && !!schemeId,
	});

	return {
		...query,
		mapping: query.data?.items?.[0] ?? null,
	};
};

// The scheme's full issue tree (types→categories→sub categories, plus any
// no-type/direct categories), for Scheme Data Entry's cascade.
export const useSchemeIssueMappingTree = (schemeId: number | string | null | undefined) => {
	const query = useQuery({
		queryKey: [...SCHEME_ISSUE_MAPPING_QUERY_KEY, "tree", schemeId],
		queryFn: () => api.fetchSchemeIssueMappingTree(schemeId as number | string),
		enabled: !!schemeId,
	});

	return {
		...query,
		tree: query.data ?? { types: [], directCategories: [] },
	};
};

export const useSchemeIssueMappingSummary = () => {
	const query = useQuery({
		queryKey: SCHEME_ISSUE_MAPPING_SUMMARY_QUERY_KEY,
		queryFn: api.fetchSchemeIssueMappingSummary,
	});

	return {
		...query,
		summary: query.data ?? [],
	};
};

export const useSaveSchemeIssueMapping = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: SchemeIssueMappingPayload) =>
			api.saveSchemeIssueMapping(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SCHEME_ISSUE_MAPPING_QUERY_KEY });
			queryClient.invalidateQueries({
				queryKey: SCHEME_ISSUE_MAPPING_SUMMARY_QUERY_KEY,
			});
		},
	});
};

export const useDeleteSchemeIssueMapping = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number | string) => api.deleteSchemeIssueMapping(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SCHEME_ISSUE_MAPPING_QUERY_KEY });
			queryClient.invalidateQueries({
				queryKey: SCHEME_ISSUE_MAPPING_SUMMARY_QUERY_KEY,
			});
		},
	});
};

export const useRemoveMappingCategory = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			mappingId,
			categoryId,
		}: {
			mappingId: number | string;
			categoryId: number | string;
		}) => api.removeMappingCategory(mappingId, categoryId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SCHEME_ISSUE_MAPPING_QUERY_KEY });
			queryClient.invalidateQueries({
				queryKey: SCHEME_ISSUE_MAPPING_SUMMARY_QUERY_KEY,
			});
		},
	});
};
